import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { pendingFemaLabel } from '../../lib/fema-pending';
import { REPORT } from '../print/PrintAtoms';
import { PORTFOLIO_TOKENS, toneStyle } from './tokens';
import { CrownPineMap, type ConnectorSegments } from './CrownPineMap';
import { PortfolioShareLinkModal, mastheadBtn, type ShareModalState } from './PortfolioPage';
import {
  fetchPortfolio,
  fetchSiteTracts,
  type PortfolioLrmGasOption,
  type PortfolioPad,
  type PortfolioResponse,
  type PortfolioRow,
  type SiteTractsBlock,
} from '../../lib/portfolioApi';

interface Props {
  clientSlug: string; // 'pine-curtain-integra' — a presentation profile over the crownpine cycle
  token?: string;
}

/** The flagship's own briefing. Path copied from the owner quick link in
    `src/pages/AdminPage.tsx` — the `?carveOut=` parameter is load-bearing:
    without it the same rep parcel renders the 55K-acre block instead of A1.
    A recipient viewing through a portfolio share token gets that same token
    appended: the server grants integra-profile portfolio tokens exactly this
    one briefing (carve-out re-forced server-side), so the CTA works the day
    the link is shared without baking any second token into the bundle. */
const A1_BRIEFING_PATH = '/briefing/912d47ae-b055-4716-9375-7946623b7f3b?carveOut=crownpine-blk-194-a1';

const a1BriefingHref = (token?: string) =>
  token ? `${A1_BRIEFING_PATH}&token=${encodeURIComponent(token)}` : A1_BRIEFING_PATH;

/** The opportunity block that carries the A1 acquisition premises. It is
    presented as the unnumbered flagship card, never as a ranked row. */
const FLAGSHIP_BLOCK_ID = 'CP-10M-015';

/** site-tracts key for the A1 premises' own LRM tract membership (computed
    against the premises geometry, not the 115-tract opportunity block —
    server counterpart in routes/crownpine-parcels.js). */
const A1_PREMISES_TRACTS_KEY = 'A1-PREMISES';

/** Decision tiers, in presentation order. Only "Recommended" opens by default;
    the counts beside each control come from the payload, never hard-coded. */
const TIERS = [
  { tier: 'Advance', label: 'Recommended' },
  { tier: 'Conditional', label: 'Conditional' },
  { tier: 'Queue', label: 'Queue' },
] as const;
// Client surfaces never show the internal tier name — "Advance" renders as
// "Recommended" everywhere Integra can see it (tabs, row chips, map labels).
const tierLabel = (tier: string) => TIERS.find(t => t.tier === tier)?.label ?? tier;

// Corridor labels. Both families were screened; neither label ranks the other.
const ET_LABEL = 'Selected ET supply corridor · direct screen';
const KM_LABEL = 'Additional screened corridor';
const GAS_CAVEAT = 'Diameter and public geometry prove neither pressure, firm capacity, tap approval, nor commercial availability.';

/** Context layers offered in the map's Layers menu, all default OFF. Keys are
    CrownPineMap's CLIENT_OVERLAY_ROWS, plus 'streams' (its own fetch-once
    NHDPlus HR layer, not a bbox overlay row). Module-level so the array
    identity is stable across renders. Fiber is a map layer only — it carries
    no ranking weight anywhere in this portfolio. */
const CLIENT_MAP_LAYERS = ['power', 'highways', 'railroads', 'airports', 'fiber', 'wells', 'streams'];

const fmt = (n: number | null | undefined, suffix = '') =>
  n == null ? '—' : `${Math.round(Number(n)).toLocaleString()}${suffix}`;

const fmtMiles = (n: number | null | undefined) =>
  n == null ? '—' : `${Number(n).toFixed(Number(n) < 10 ? 2 : 1)} mi`;

const gasHeadline = (o: PortfolioLrmGasOption) => {
  const dia = o.diameter_in == null
    ? null
    : `${Number(o.diameter_in).toLocaleString(undefined, { maximumFractionDigits: 2 })}″`;
  // The corridor name alone doesn't say who you would be negotiating with, so
  // the owning family rides with it. Falls back to the raw operator.
  const owner = o.operator_parent || o.operator;
  return [o.name, owner, dia, fmtMiles(o.campus_distance_mi)].filter(Boolean).join(' · ');
};

// How much of the screened route runs on land the seller supplied as their own
// inventory. Absent miles read as unavailable rather than as clean.
const gasRouteSplit = (o: PortfolioLrmGasOption) => {
  if (o.cp_mi == null && o.outside_mi == null) return 'Route split unavailable';
  const parts = [
    o.cp_mi == null ? null : `${Number(o.cp_mi).toFixed(2)} mi on supplied Crown Pine inventory`,
    o.outside_mi == null ? null : `${Number(o.outside_mi).toFixed(2)} mi outside`,
  ].filter(Boolean);
  return parts.join(' · ');
};

const GasCell: React.FC<{ eyebrow: string; option: PortfolioLrmGasOption | null }> = ({ eyebrow, option }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: 7.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: REPORT.tertiary }}>
      {eyebrow}
    </div>
    {option ? (
      <>
        <div style={{ marginTop: 1, color: REPORT.heading, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {gasHeadline(option)}
        </div>
        <div style={{ marginTop: 1, color: REPORT.secondary }}>{gasRouteSplit(option)}</div>
        {option.crosses_block && (
          <div style={{ marginTop: 1, color: REPORT.secondary }}>Crosses the opportunity block</div>
        )}
      </>
    ) : (
      <div style={{ marginTop: 1, color: REPORT.secondary }}>Not screened</div>
    )}
  </div>
);

interface RowProps {
  pad: PortfolioPad & { shortlist: NonNullable<PortfolioPad['shortlist']> };
  isActive: boolean;
  isHovered: boolean;
  onSelect: (padId: string) => void;
  onHover: (padId: string | null) => void;
  /** Crown Pine's own LRM tract membership for this block (null while loading). */
  tracts: SiteTractsBlock | null;
}

// Fund strings arrive as "CPT1-Crown Pine Timber 1 LP" — the display form is
// the legal entity after the code.
const fundEntity = (fund: string) =>
  fund.includes('-') ? fund.slice(fund.indexOf('-') + 1) : fund;

/** The seller's LRM tract identifiers for a site — shared by the ranked rows
    and the flagship card so both name land in Crown Pine's own numbers. */
const TractsOfRecord: React.FC<{ tracts: SiteTractsBlock | null }> = ({ tracts }) => {
  if (!tracts || tracts.count === 0) return null;
  return (
    <div style={{ marginTop: 4, paddingTop: 4, borderTop: `1px solid ${PORTFOLIO_TOKENS.ruleSoft}` }}>
      <div style={{ color: REPORT.body }}>
        Crown Pine tracts of record: {tracts.count}
        {Object.entries(tracts.funds).map(([f, n]) => ` · ${fundEntity(f)} ×${n}`).join('')}
        {tracts.partial_count > 0 ? ` · ${tracts.partial_count} in part` : ''}
      </div>
      <div style={{ marginTop: 1, lineHeight: 1.6 }}>
        {tracts.tracts.map(t => t.name + (t.partial ? ' (part)' : '')).join(' · ')}
      </div>
    </div>
  );
};

// One shortlisted site, client presentation. Deliberately NOT CrownPineRow:
// that row is the research view (its gas cell reads the legacy single-pipeline
// field and hard-labels the operator). This one reads the curated ET/KM screen.
export const IntegraSiteRow: React.FC<RowProps> = ({ pad, isActive, isHovered, onSelect, onHover, tracts }) => {
  const s = pad.shortlist;
  const pendingFlood = s.flood_basis?.status === 'pending' ? s.flood_basis : null;
  const tone = toneStyle(s.tier === 'Advance' ? 'pass' : 'caution');
  const campus = s.campus;
  const buildable = s.buildable ?? null;
  const effective = buildable?.effective;
  // The headline is every buildable acre in the block. Sites screened before
  // that measure existed fall back to the placement, labelled as such — the two
  // numbers mean different things and must never wear the same word.
  const headlineAc = buildable ? buildable.total_ac : campus.acres;
  const headlineLabel = buildable ? 'buildable ac' : 'screened ac';
  // The placement is where the slope and fill numbers were actually measured,
  // so it stays on the row as context under the headline.
  const placement = [
    `${fmt(campus.acres)} ac`,
    campus.avg_slope_pct == null ? null : `${Number(campus.avg_slope_pct).toFixed(1)}% avg slope`,
    campus.pct_under_5_pct == null ? null : `${Number(campus.pct_under_5_pct).toFixed(0)}% under 5%`,
    campus.estimated_aquatic_fill_ac && campus.estimated_aquatic_fill_ac > 0
      ? `${Number(campus.estimated_aquatic_fill_ac).toFixed(2)} ac est. fill`
      : null,
  ].filter(Boolean).join(' · ');
  const femaLine = [
    s.outside_sfha_ac == null ? 'Block SFHA acreage unavailable' : `${fmt(s.outside_sfha_ac)} block ac outside SFHA`,
    pendingFlood ? pendingFemaLabel(pendingFlood.effective_date) : s.fema_coverage_pct == null ? 'FEMA coverage unavailable' : `${Number(s.fema_coverage_pct).toFixed(0)}% FEMA coverage`,
  ].join(' · ');
  // Air-permitting chip. Rendered only when the seed actually ran the EPA NAAQS
  // screen — an unscreened site shows nothing rather than an implied pass.
  const attainment = s.attainment ?? null;
  const attainmentTone = attainment ? toneStyle(attainment.status === 'attainment' ? 'pass' : 'caution') : null;
  const attainmentDetail = attainment
    ? attainment.status === 'attainment'
      ? 'no EPA nonattainment or maintenance area overlaps the opportunity block'
      : attainment.overlaps.map(o => (o.pollutant ? `${o.area} (${o.pollutant})` : o.area)).join('; ')
    : null;

  return (
    <div
      data-parcel-row-id={pad.id}
      onClick={() => onSelect(pad.id)}
      onMouseEnter={() => onHover(pad.id)}
      onMouseLeave={() => onHover(null)}
      style={{
        borderStyle: 'solid',
        borderWidth: '1px 1px 1px 3px',
        borderColor: `${isActive ? REPORT.accent : REPORT.rule} ${isActive ? REPORT.accent : REPORT.rule} ${isActive ? REPORT.accent : REPORT.rule} ${tone.fg}`,
        background: isActive ? REPORT.surface : isHovered ? '#fbfaf8' : REPORT.white,
        padding: '9px 10px 10px', cursor: 'pointer',
        transition: 'background 0.12s ease, border-color 0.12s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8, fontWeight: 700,
          letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap',
          color: tone.fg, background: tone.bgWash,
          border: `1px solid ${tone.border}`, borderRadius: 3, padding: '1px 5px',
        }}>
          {tierLabel(s.tier)}
        </span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 14, color: REPORT.heading,
          flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {s.block_id}
        </span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, color: REPORT.tertiary, whiteSpace: 'nowrap',
        }}>
          {s.county || pad.parent?.county} County
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 7, rowGap: 2, marginTop: 5 }}>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 13, fontWeight: 700, color: tone.fg }}>
          {fmt(headlineAc)}
        </span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.2, letterSpacing: '0.05em', color: REPORT.tertiary, textTransform: 'uppercase' }}>
          {headlineLabel}
        </span>
        <span style={{ color: REPORT.rule, margin: '0 1px' }}>|</span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 10, fontWeight: 700, color: REPORT.heading }}>
          {fmt(s.gross_legal_acres)}
        </span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.2, letterSpacing: '0.05em', color: REPORT.tertiary, textTransform: 'uppercase' }}>
          gross block ac
        </span>
        {((buildable?.fema_absent && !pendingFlood) || buildable?.nwi_absent) && (
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.2, color: REPORT.tertiary }}>
            · screened vs available layers — {[
              buildable.fema_absent && !pendingFlood ? 'FEMA digital coverage' : null,
              buildable.nwi_absent ? 'NWI wetlands mapping' : null,
            ].filter(Boolean).join(' and ')} absent
          </span>
        )}
      </div>

      <div style={{ marginTop: 4, fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.8, color: REPORT.secondary }}>
        <div>{femaLine}</div>
        {effective && effective.crossings > 0 && (
          <div style={{ marginTop: 1 }}>
            Effective contiguous: {fmt(effective.total_ac)} ac in {effective.piece_count} piece{effective.piece_count === 1 ? '' : 's'} · {effective.crossings} stream crossing{effective.crossings === 1 ? '' : 's'}
          </div>
        )}
        <div style={{ marginTop: 1 }}>Screened placement: {placement}</div>
        <TractsOfRecord tracts={tracts} />
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 7,
        padding: '6px 7px', background: REPORT.surface, borderTop: `1px solid ${PORTFOLIO_TOKENS.ruleSoft}`,
        fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.8, color: REPORT.secondary,
      }}>
        <GasCell eyebrow={ET_LABEL} option={s.gas?.primary ?? null} />
        <GasCell eyebrow={KM_LABEL} option={s.gas?.additional ?? null} />
        {s.gas?.primary?.curation_note && (
          <div style={{ gridColumn: '1 / -1', color: REPORT.body, lineHeight: 1.45 }}>
            {s.gas.primary.curation_note}
          </div>
        )}
        <div style={{ gridColumn: '1 / -1', fontSize: 8, color: REPORT.tertiary, lineHeight: 1.4 }}>
          {GAS_CAVEAT}
        </div>
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '76px 1fr', columnGap: 7, rowGap: 3,
        marginTop: 6, fontSize: 9.5, lineHeight: 1.35,
      }}>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.8, letterSpacing: '0.07em', textTransform: 'uppercase', color: REPORT.tertiary }}>Highway</span>
        <span style={{ color: REPORT.body }}>
          {s.highway.name || '—'} · {fmtMiles(s.highway.campus_distance_mi)}
          {s.highway.note ? ` · ${s.highway.note}` : ''}
        </span>
        {attainment && attainmentTone && (
          <>
            <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.8, letterSpacing: '0.07em', textTransform: 'uppercase', color: REPORT.tertiary }}>TCEQ air</span>
            <span style={{ color: REPORT.body }}>
              <span style={{
                fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.8, fontWeight: 700,
                letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap',
                color: attainmentTone.fg, background: attainmentTone.bgWash,
                border: `1px solid ${attainmentTone.border}`, borderRadius: 3,
                padding: '0 4px', marginRight: 5,
              }}>
                {attainment.status}
              </span>
              {attainmentDetail}
            </span>
          </>
        )}
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.8, letterSpacing: '0.07em', textTransform: 'uppercase', color: REPORT.tertiary }}>Expansion</span>
        <span style={{ color: REPORT.body }}>{s.expansion_status || '—'}</span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 7.8, letterSpacing: '0.07em', textTransform: 'uppercase', color: REPORT.tertiary }}>Primary gate</span>
        <span style={{ color: REPORT.body }}>{s.primary_gate || '—'}</span>
      </div>
    </div>
  );
};

// The A1 acquisition premises. The premises acreage, parcel count and band
// crossing are the A1 briefing's canonical figures, not screen output, and the
// briefing itself is the document those numbers live in — only the buildable
// headline comes off the payload. Clicking the card selects and zooms the
// flagship's block exactly as a ranked row does.
const FlagshipCard: React.FC<{
  padId: string | null;
  buildableAc: number | null;
  isActive: boolean;
  onSelect: (padId: string) => void;
  token?: string;
  /** The premises' own LRM tract membership (null while loading). */
  tracts: SiteTractsBlock | null;
}> = ({ padId, buildableAc, isActive, onSelect, token, tracts }) => {
  const tone = toneStyle('pass');
  return (
    <div
      data-parcel-row-id={padId ?? undefined}
      onClick={() => { if (padId) onSelect(padId); }}
      style={{
        borderStyle: 'solid',
        borderWidth: '1px 1px 1px 3px',
        borderColor: `${isActive ? REPORT.accent : REPORT.rule} ${isActive ? REPORT.accent : REPORT.rule} ${isActive ? REPORT.accent : REPORT.rule} ${tone.fg}`,
        background: isActive ? REPORT.surface : REPORT.white,
        padding: '10px 12px 11px',
        marginBottom: 10,
        cursor: padId ? 'pointer' : 'default',
        transition: 'background 0.12s ease, border-color 0.12s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8, fontWeight: 700,
          letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap',
          color: tone.fg, background: tone.bgWash,
          border: `1px solid ${tone.border}`, borderRadius: 3, padding: '1px 5px',
        }}>
          Flagship
        </span>
        <span style={{
          fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 15, color: REPORT.heading,
          flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          A1 — Crown Pine flagship
        </span>
        <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, color: REPORT.tertiary, whiteSpace: 'nowrap' }}>
          Angelina County
        </span>
      </div>

      {buildableAc != null && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 6 }}>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 13, fontWeight: 700, color: tone.fg }}>
            {fmt(buildableAc)}
          </span>
          <span style={{ fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.2, letterSpacing: '0.05em', color: REPORT.tertiary, textTransform: 'uppercase' }}>
            buildable ac across the opportunity block
          </span>
        </div>
      )}

      <div style={{ marginTop: 6, fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.2, color: REPORT.body, lineHeight: 1.6 }}>
        <div>3,037.93 ac premises · 7 parcels of record (1 in part)</div>
        <div>ET Texoma 30&#8243; crosses the premises · ~0.52 mi</div>
        <TractsOfRecord tracts={tracts} />
      </div>

      <a
        href={a1BriefingHref(token)}
        target="_blank"
        rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        style={{
          display: 'inline-block', marginTop: 9, padding: '5px 11px',
          fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none',
          color: REPORT.white, background: REPORT.heading, borderRadius: 3,
        }}
      >
        Open A1 briefing &rarr;
      </a>
    </div>
  );
};

// Crown Pine Site Portfolio — the Integra-facing presentation of the canonical
// `crownpine` cycle. Same data, different surface: the flagship premises, then
// the screened sites in decision tiers, with the curated ET/KM corridors and
// their land-control split drawn on the map.
export const PineCurtainIntegraPage: React.FC<Props> = ({ clientSlug, token }) => {
  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openTiers, setOpenTiers] = useState<Set<string>>(() => new Set(['Advance']));
  const [activePadId, setActivePadId] = useState<string | null>(null);
  const [hoveredPadId, setHoveredPadId] = useState<string | null>(null);
  const [shareModal, setShareModal] = useState<ShareModalState>({ open: false });

  const contentRef = useRef<HTMLDivElement>(null);
  const [mapH, setMapH] = useState(560);
  useLayoutEffect(() => {
    const measure = () => {
      const h = contentRef.current?.clientHeight;
      // Offset covers the map's toolbar row + the legend row beneath it + borders.
      if (h) setMapH(Math.max(360, h - 70));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [data]);

  useEffect(() => {
    let mounted = true;
    fetchPortfolio(clientSlug, token)
      .then(d => { if (mounted) setData(d); })
      .catch(e => { if (mounted) setError(e.message || 'Failed to load'); });
    return () => { mounted = false; };
  }, [clientSlug, token]);

  // Crown Pine's own LRM tract identifiers per block — non-fatal enrichment;
  // the page renders fully without it and the rows fill in when it lands.
  const [siteTracts, setSiteTracts] = useState<Record<string, SiteTractsBlock> | null>(null);
  useEffect(() => {
    let mounted = true;
    fetchSiteTracts(token)
      .then(d => { if (mounted) setSiteTracts(d.blocks); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [token]);

  type ShortlistPad = PortfolioPad & { shortlist: NonNullable<PortfolioPad['shortlist']> };
  const shortlistPads = useMemo<ShortlistPad[]>(() => {
    if (!data?.pads) return [];
    return data.pads
      .filter((p): p is ShortlistPad => p.shortlist != null)
      .slice()
      .sort((a, b) => a.shortlist.screen_rank - b.shortlist.screen_rank);
  }, [data]);

  const flagshipPad = useMemo(
    () => shortlistPads.find(p => p.shortlist.block_id === FLAGSHIP_BLOCK_ID) ?? null,
    [shortlistPads]
  );
  const tierGroups = useMemo(
    () => TIERS.map(t => ({
      ...t,
      pads: shortlistPads.filter(p => p !== flagshipPad && p.shortlist.tier === t.tier),
    })),
    [shortlistPads, flagshipPad]
  );
  const visiblePads = useMemo(
    () => tierGroups.filter(g => openTiers.has(g.tier)).flatMap(g => g.pads),
    [tierGroups, openTiers]
  );
  // The flagship stays on the map whatever the tier toggles say — it is the
  // portfolio's anchor, not one of the ranked rows.
  const mapPads = useMemo(
    () => (flagshipPad ? [flagshipPad, ...visiblePads] : visiblePads),
    [flagshipPad, visiblePads]
  );

  // Map rows carry the screened OPPORTUNITY BLOCK outline (the land estate);
  // the campus footprint is the pad layer. Centroids are dropped so the client
  // map draws land, not research markers, and the research grade/MW columns are
  // nulled rather than shown empty.
  const mapRows = useMemo<PortfolioRow[]>(() => {
    if (!data) return [];
    const rowsByParcel = new Map(data.rows.map(row => [row.parcel_id, row]));
    return mapPads.flatMap(pad => {
      const parcelId = pad.parent?.parcel_id;
      const row = parcelId ? rowsByParcel.get(parcelId) : undefined;
      if (!row) return [];
      return [{
        ...row,
        rank: pad.shortlist.screen_rank,
        site_label: pad.shortlist.block_id,
        priority_label: tierLabel(pad.shortlist.tier),
        key_numbers: { ...row.key_numbers, acres: pad.shortlist.gross_legal_acres, available_mw: null },
        knockouts: { ...row.knockouts, count: pad.shortlist.tier === 'Advance' ? 0 : 1, list: [] },
        score: null,
        grade: null,
        centroid: null,
        geometry: pad.shortlist.block_geometry,
        member_geometries: null,
      }];
    });
  }, [data, mapPads]);

  const mapBbox = useMemo<[number, number, number, number] | null>(() => {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    const walk = (c: unknown): void => {
      if (Array.isArray(c) && typeof c[0] === 'number') {
        const [x, y] = c as number[];
        if (x < minX) minX = x; if (y < minY) minY = y;
        if (x > maxX) maxX = x; if (y > maxY) maxY = y;
      } else if (Array.isArray(c)) {
        for (const k of c) walk(k);
      }
    };
    for (const pad of mapPads) walk((pad.shortlist.block_geometry as { coordinates?: unknown }).coordinates);
    if (!isFinite(minX)) return data?.synthesis?.bbox ?? null;
    return [minX, minY, maxX, maxY];
  }, [mapPads, data]);

  const activePad = useMemo(
    () => (activePadId ? mapPads.find(p => p.id === activePadId) ?? null : null),
    [mapPads, activePadId]
  );

  // Only the selected site's connector draws — a map of thirteen screened
  // routes at once reads as noise, and the caveat belongs to one route at a time.
  const connector = useMemo<ConnectorSegments | null>(() => {
    const primary = activePad?.shortlist.gas?.primary;
    if (!primary) return null;
    if (!primary.cp_segments && !primary.outside_segments) return null;
    // The dashed run's hover names whose land it crosses. Longest crossings
    // lead; the list caps so a many-owner run stays a tooltip, not a deed index.
    const ownerScreen = primary.outside_owner_screen ?? null;
    let outsideDetail: string | null = null;
    if (ownerScreen && ownerScreen.owners.length > 0) {
      const top = ownerScreen.owners.slice(0, 6).map(o =>
        o.mi != null && o.mi >= 0.01 ? `${o.name} · ${Number(o.mi).toFixed(2)} mi` : o.name);
      const more = ownerScreen.owners.length - top.length;
      if (more > 0) top.push(`+ ${more} more owner${more === 1 ? '' : 's'} of record`);
      outsideDetail = `Crosses ${ownerScreen.parcel_count} parcel${ownerScreen.parcel_count === 1 ? '' : 's'} of record:\n${top.join('\n')}`;
    }
    return {
      cp: primary.cp_segments ?? null,
      outside: primary.outside_segments ?? null,
      cpLabel: primary.cp_mi == null
        ? 'On supplied Crown Pine inventory'
        : `${Number(primary.cp_mi).toFixed(2)} mi on supplied Crown Pine inventory`,
      outsideLabel: primary.outside_mi == null
        ? 'Outside supplied Crown Pine inventory'
        : `${Number(primary.outside_mi).toFixed(2)} mi outside supplied Crown Pine inventory`,
      outsideDetail,
    };
  }, [activePad]);

  const toggleTier = (tier: string) => {
    setOpenTiers(prev => {
      const next = new Set(prev);
      if (next.has(tier)) next.delete(tier); else next.add(tier);
      return next;
    });
  };

  // A tier closing under the selection would leave the map ringing a site the
  // list no longer shows.
  useEffect(() => {
    if (activePadId && !mapPads.some(p => p.id === activePadId)) setActivePadId(null);
  }, [mapPads, activePadId]);

  useEffect(() => {
    if (!activePadId) return;
    const el = document.querySelector(`[data-parcel-row-id="${CSS.escape(activePadId)}"]`);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activePadId]);

  if (error) {
    return (
      <div style={{ padding: 80, textAlign: 'center', fontFamily: PORTFOLIO_TOKENS.sans, color: REPORT.body }}>
        <h1 style={{ fontFamily: PORTFOLIO_TOKENS.serif, color: REPORT.heading, fontWeight: 400 }}>Portfolio unavailable</h1>
        <p style={{ marginTop: 12, color: REPORT.secondary }}>{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: 80, textAlign: 'center', fontFamily: PORTFOLIO_TOKENS.mono, color: REPORT.tertiary, fontSize: 11, letterSpacing: '0.10em', textTransform: 'uppercase' }}>
        Loading portfolio…
      </div>
    );
  }

  return (
    <div style={{
      background: REPORT.white,
      height: '100vh',
      overflow: 'hidden',
      fontFamily: PORTFOLIO_TOKENS.sans,
      color: REPORT.body,
      fontSize: 12.5,
      lineHeight: 1.5,
      padding: '12px 20px 0',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Masthead — the production Atwater × Integra co-brand lockup, reused
          verbatim from PortfolioPage. This is the client deliverable. */}
      <header style={{
        background: REPORT.heading,
        borderRadius: 4,
        padding: '9px 18px',
        marginBottom: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 18, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <img
              src="/atwater-logo-light.svg"
              alt="Atwater Energy"
              style={{ height: 28, width: 'auto', display: 'block' }}
            />
            <span style={{
              fontFamily: PORTFOLIO_TOKENS.serif,
              fontSize: 16,
              fontStyle: 'italic',
              color: PORTFOLIO_TOKENS.patina,
              opacity: 0.75,
              lineHeight: 1,
            }}>
              ×
            </span>
            <img
              src="/integra-logo-light.webp"
              alt="Integra"
              style={{ height: 16, width: 'auto', display: 'block', opacity: 0.95 }}
            />
          </div>
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.serif, fontSize: 17, color: '#fff', whiteSpace: 'nowrap',
          }}>
            Pine Curtain Project Portfolio
          </span>
          <span style={{
            fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 9.5, fontWeight: 500, letterSpacing: '0.10em',
            textTransform: 'uppercase', color: PORTFOLIO_TOKENS.patina, whiteSpace: 'nowrap',
            borderLeft: '1px solid rgba(212,152,128,0.28)', paddingLeft: 14,
          }}>
            Large-Campus Opportunities in lands owned by Crown Pine
          </span>
        </div>
        {!token && (
          <button type="button" style={mastheadBtn} onClick={() => setShareModal({ open: true })}>
            Share
          </button>
        )}
      </header>

      {shareModal.open && (
        <PortfolioShareLinkModal
          clientSlug={clientSlug}
          state={shareModal}
          onChange={setShareModal}
          onClose={() => setShareModal({ open: false })}
        />
      )}

      <div ref={contentRef} style={{ flex: 1, minHeight: 0, display: 'flex', gap: 14, paddingBottom: 12 }}>
        <div style={{ flex: '1 1 62%', minWidth: 0 }}>
          <CrownPineMap
            rows={mapRows}
            pads={mapPads}
            cycleBbox={mapBbox}
            onSelectPad={setActivePadId}
            onHoverPad={setHoveredPadId}
            activePadId={activePadId}
            focusPadId={activePadId}
            hoveredPadId={hoveredPadId}
            sticky={false}
            height={mapH}
            shareToken={token}
            hideResearchBaseLayers
            pipelineFamilyStyling
            pipelineUnfiltered
            measureControl
            clientOverlays={CLIENT_MAP_LAYERS}
            connector={connector}
            buildableHighlight
            lrmTractsDefaultOn={false}
            tierMarkerLegend
          />
        </div>

        <div style={{ flex: '1 1 38%', minWidth: 360, maxWidth: 560, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {/* Tier controls — Recommended opens by default; the other two are
              opt-in. Counts come straight off the payload. */}
          <div style={{ display: 'flex', flexShrink: 0, marginBottom: 8, border: `1px solid ${REPORT.rule}`, borderRadius: 4, overflow: 'hidden' }}>
            {tierGroups.map(g => {
              const on = openTiers.has(g.tier);
              return (
                <button
                  key={g.tier}
                  type="button"
                  onClick={() => toggleTier(g.tier)}
                  style={{
                    flex: 1, padding: '7px 8px', border: 'none', cursor: 'pointer',
                    fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.3, fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    background: on ? REPORT.heading : REPORT.white,
                    color: on ? '#fff' : REPORT.secondary,
                    transition: 'background 0.12s ease', whiteSpace: 'nowrap',
                  }}
                >
                  {g.label} · {g.pads.length}
                </button>
              );
            })}
          </div>

          <div style={{
            flexShrink: 0, fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, letterSpacing: '0.06em',
            color: REPORT.tertiary, padding: '0 4px 8px', lineHeight: 1.5,
          }}>
            Each site leads with the buildable acreage screened across its whole opportunity block,
            with mapped wetlands, floodplain, state-maintained road corridors, and 1,000-ft buffers
            around non-plugged oil &amp; gas wells netted out where those layers exist, against the
            gross block acreage. Buildable areas joined only by a sliver narrower than about 100 ft —
            a road-corridor width — are reported as separate pieces.
            The screened placement beneath it is where the slope and fill figures
            were measured. Corridor miles are a straight-line screen against supplied Crown Pine
            inventory, not a surveyed route. Fiber carries no weight here.
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', paddingRight: 4, paddingBottom: 24 }}>
            <FlagshipCard
              padId={flagshipPad?.id ?? null}
              buildableAc={flagshipPad?.shortlist.buildable?.total_ac ?? null}
              isActive={flagshipPad != null && flagshipPad.id === activePadId}
              onSelect={setActivePadId}
              token={token}
              tracts={siteTracts?.[A1_PREMISES_TRACTS_KEY] ?? null}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {tierGroups.filter(g => openTiers.has(g.tier)).map(g => (
                <React.Fragment key={g.tier}>
                  <div style={{
                    fontFamily: PORTFOLIO_TOKENS.mono, fontSize: 8.5, fontWeight: 700,
                    letterSpacing: '0.10em', textTransform: 'uppercase', color: REPORT.tertiary,
                    padding: '6px 4px 2px', borderBottom: `1px solid ${REPORT.rule}`,
                  }}>
                    {g.label} · {g.pads.length}
                  </div>
                  {g.pads.map(pad => (
                    <IntegraSiteRow
                      key={pad.id}
                      pad={pad}
                      isActive={pad.id === activePadId}
                      isHovered={pad.id === hoveredPadId}
                      onSelect={setActivePadId}
                      onHover={setHoveredPadId}
                      tracts={siteTracts?.[pad.shortlist.block_id] ?? null}
                    />
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PineCurtainIntegraPage;
